import { useCallback, useEffect, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { digitooApi, filesApi, movementsApi } from '@/api/endpoints'
import type { DocumentFile, MovementDetail, MovementStatus } from '@/api/types'
import { Button, Card, Textarea } from '@/components/ui'
import StatusBadge, { ExportBadge } from '@/components/StatusBadge'
import { useListStore } from '@/stores/useListStore'
import { errorMessage } from '@/utils/errors'
import { formatBytes, formatDate, formatDateTime, formatMoney, STATUS_LABELS } from '@/utils/format'

/** Stavy, které jde nastavit ručně. „Doloženo“ se nastaví samo nahráním dokladu. */
const MANUAL_STATUSES: MovementStatus[] = ['MISSING', 'NOT_NEEDED', 'UNTRACEABLE']

export default function MovementDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { ids, search } = useListStore()

  const [m, setM] = useState<MovementDetail | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [showFields, setShowFields] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const apply = (detail: MovementDetail) => {
    setM(detail)
    setNote(detail.note ?? '')
  }

  useEffect(() => {
    setM(null)
    setError(null)
    setConfirmDelete(null)
    movementsApi
      .detail(id)
      .then(apply)
      .catch((e) => setError(errorMessage(e, 'Pohyb se nepodařilo načíst.')))
  }, [id])

  // Doklad ve frontě do Digitoo — obnovujeme, dokud neodejde.
  const pending = m?.exportState === 'PENDING'
  useEffect(() => {
    if (!pending) return
    const t = setInterval(() => {
      movementsApi
        .detail(id)
        .then((d) => setM((prev) => (prev ? { ...d, note: prev.note } : d)))
        .catch(() => undefined)
    }, 4000)
    return () => clearInterval(t)
  }, [pending, id])

  const sendToDigitoo = async (fileId: string) => {
    setError(null)
    try {
      const d = await digitooApi.sendFile(fileId)
      setM((prev) => (prev ? { ...d, note: prev.note } : d))
    } catch (e) {
      setError(errorMessage(e, 'Doklad se nepodařilo zařadit k odeslání.'))
    }
  }

  const index = ids.indexOf(id)
  const prevId = index > 0 ? ids[index - 1] : null
  const nextId = index >= 0 && index < ids.length - 1 ? ids[index + 1] : null
  const backTo = `/doklady${search ? `?${search}` : ''}`

  const upload = useCallback(
    async (files: File[]) => {
      if (!files.length) return
      setError(null)
      setProgress(0)
      try {
        apply(await filesApi.upload(id, files, setProgress))
      } catch (e) {
        setError(errorMessage(e, 'Doklad se nepodařilo nahrát.'))
      } finally {
        setProgress(null)
      }
    },
    [id],
  )

  // Vložení ze schránky (Ctrl+V screenshotu nebo souboru) rovnou nahraje doklad.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return
      const files = Array.from(e.clipboardData?.files ?? [])
      if (files.length) {
        e.preventDefault()
        upload(files)
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [upload])

  const setStatus = async (status: MovementStatus) => {
    setBusy(true)
    setError(null)
    try {
      apply(await movementsApi.update(id, { status, note }))
    } catch (e) {
      setError(errorMessage(e, 'Stav se nepodařilo uložit.'))
    } finally {
      setBusy(false)
    }
  }

  const saveNote = async () => {
    if (!m || (m.note ?? '') === note) return
    setBusy(true)
    try {
      apply(await movementsApi.update(id, { note }))
    } catch (e) {
      setError(errorMessage(e, 'Poznámku se nepodařilo uložit.'))
    } finally {
      setBusy(false)
    }
  }

  const removeFile = async (fileId: string) => {
    if (confirmDelete !== fileId) return setConfirmDelete(fileId)
    setConfirmDelete(null)
    try {
      apply(await filesApi.remove(fileId))
    } catch (e) {
      setError(errorMessage(e, 'Doklad se nepodařilo smazat.'))
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragging(false)
    upload(Array.from(e.dataTransfer.files))
  }

  if (!m) {
    return (
      <div className="space-y-4">
        <Link to={backTo} className="text-sm text-brand-600 hover:underline">
          ← Zpět na seznam
        </Link>
        {error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-slate-400">Načítání…</p>}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={backTo} className="text-sm text-brand-600 hover:underline">
          ← Zpět na seznam
        </Link>
        {index >= 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>
              {index + 1} / {ids.length}
            </span>
            <Button variant="secondary" disabled={!prevId} onClick={() => prevId && navigate(`/doklady/${prevId}`)}>
              ←
            </Button>
            <Button variant="secondary" disabled={!nextId} onClick={() => nextId && navigate(`/doklady/${nextId}`)}>
              Další →
            </Button>
          </div>
        )}
      </div>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <StatusBadge status={m.status} />
              <ExportBadge state={m.exportState} />
              <span className="text-sm text-slate-400">{m.kod}</span>
            </div>
            <h1 className="truncate text-xl font-semibold">{m.companyName ?? 'Bez firmy'}</h1>
            <p className="text-slate-600">{m.description}</p>
          </div>
          <div className="text-right">
            <div
              className={`text-2xl font-semibold tabular-nums ${
                m.direction === 'OUT' ? 'text-red-700' : m.direction === 'IN' ? 'text-green-700' : ''
              }`}
            >
              {formatMoney(m.amount, m.currency, m.direction)}
            </div>
            {m.amountForeign !== null && (
              <div className="text-sm text-slate-500">{m.amountForeign.toLocaleString('cs-CZ')} v cizí měně</div>
            )}
            <div className="text-sm text-slate-500">{formatDate(m.issuedOn)}</div>
          </div>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Info label="Variabilní symbol" value={m.varSym} mono />
          <Info label="Specifický symbol" value={m.specSym} mono />
          <Info label="Protiúčet" value={m.counterAccount} mono />
          <Info label="Náš účet" value={m.bankAccount} />
          <Info label="IČO" value={m.companyIc} mono />
          <Info label="Přílohy ve FlexiBee" value={m.attachmentsInFlexi?.toString() ?? null} />
          {m.flexiNote && (
            <div className="sm:col-span-2 lg:col-span-3">
              <Info label="Poznámka ve FlexiBee" value={m.flexiNote} />
            </div>
          )}
        </dl>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={dragging ? 'border-brand-500 ring-2 ring-brand-200' : ''}
        >
          <h2 className="mb-3 font-semibold">Doklady</h2>

          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 px-4 py-8 text-sm text-slate-500 transition hover:border-brand-500 hover:text-brand-600"
          >
            {progress !== null ? (
              <span>Nahrávám… {progress} %</span>
            ) : (
              <>
                <span className="font-medium">Přetáhni sem doklad nebo klikni</span>
                <span className="text-xs text-slate-400">PDF, fotka, ISDOC… (jde i Ctrl+V ze schránky)</span>
              </>
            )}
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              upload(Array.from(e.target.files ?? []))
              e.target.value = ''
            }}
          />

          {m.files.length > 0 && (
            <ul className="mt-4 divide-y divide-slate-100">
              {m.files.map((f) => (
                <li key={f.id} className="py-2 text-sm">
                  <a
                    href={filesApi.url(f.id, true)}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-medium text-brand-700 hover:underline"
                  >
                    {f.fileName}
                  </a>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-xs text-slate-400">
                      {formatBytes(f.sizeBytes)} · {formatDateTime(f.createdAt)}
                      {f.uploadedByEmail && ` · ${f.uploadedByEmail}`}
                      {f.exportedAt && ` · v ZIPu ${formatDateTime(f.exportedAt)}`}
                    </span>
                    <DigitooState file={f} onSend={() => sendToDigitoo(f.id)} />
                    <span className="ml-auto flex gap-3">
                      <a href={filesApi.url(f.id)} className="text-xs text-brand-600 hover:underline">
                        stáhnout
                      </a>
                      <button
                        onClick={() => removeFile(f.id)}
                        onBlur={() => setConfirmDelete(null)}
                        className={`text-xs hover:underline ${confirmDelete === f.id ? 'font-semibold text-red-700' : 'text-red-500'}`}
                      >
                        {confirmDelete === f.id ? 'opravdu smazat?' : 'smazat'}
                      </button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold">Zpracování</h2>
          <div className="mb-4 flex flex-wrap gap-2">
            {MANUAL_STATUSES.map((s) => (
              <Button
                key={s}
                variant={m.status === s ? 'primary' : 'secondary'}
                disabled={busy || m.status === s}
                onClick={() => setStatus(s)}
              >
                {STATUS_LABELS[s]}
              </Button>
            ))}
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Poznámka</span>
            <Textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              placeholder="Např. faktura vyžádána e-mailem 3. 10., soukromý výdaj…"
            />
          </label>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
            <span>
              {m.updatedByEmail && `Naposledy ${m.updatedByEmail}, ${formatDateTime(m.updatedAt)}`}
            </span>
            {(m.note ?? '') !== note && (
              <Button variant="secondary" onClick={saveNote} disabled={busy}>
                Uložit poznámku
              </Button>
            )}
          </div>
          {nextId && m.status !== 'MISSING' && (
            <Button className="mt-4 w-full" onClick={() => navigate(`/doklady/${nextId}`)}>
              Hotovo, další →
            </Button>
          )}
        </Card>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Card>
        <button className="text-sm font-medium text-slate-600 hover:text-slate-800" onClick={() => setShowFields(!showFields)}>
          {showFields ? '▾' : '▸'} Všechna pole z FlexiBee ({m.fields.length})
        </button>
        {showFields && (
          <table className="mt-3 w-full text-sm">
            <tbody>
              {m.fields.map((f) => (
                <tr key={f.name} className="border-t border-slate-100 align-top">
                  <td className="w-48 py-1.5 pr-4 font-mono text-xs text-slate-500">{f.name}</td>
                  <td className="break-all py-1.5">
                    {f.showAs ?? f.value}
                    {f.showAs && <span className="ml-2 text-xs text-slate-400">{f.value}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}

function Info({ label, value, mono = false }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={`${mono ? 'font-mono' : ''} ${value ? '' : 'text-slate-300'}`}>{value || '—'}</dd>
    </div>
  )
}

/** Stav dokladu vůči Digitoo + tlačítko (znovu) odeslat. */
function DigitooState({ file, onSend }: { file: DocumentFile; onSend: () => void }) {
  const button = (label: string) => (
    <button onClick={onSend} className="text-xs text-brand-600 hover:underline">
      {label}
    </button>
  )
  if (file.sendStatus === 'QUEUED') return <span className="text-xs text-violet-700">odesílá se do Digitoo…</span>
  return (
    <span className="flex flex-wrap items-center gap-x-2 text-xs">
      {file.sentAt && <span className="text-sky-700">v Digitoo {formatDateTime(file.sentAt)}</span>}
      {file.sendStatus === 'FAILED' && !file.sentAt && (
        <span className="text-red-600" title={file.sendError ?? undefined}>
          neodešlo{file.sendError ? `: ${file.sendError}` : ''}
        </span>
      )}
      {button(file.sentAt ? 'poslat znovu' : file.sendStatus === 'FAILED' ? 'zkusit znovu' : 'poslat do Digitoo')}
    </span>
  )
}
